import { useCallback, useMemo, useState } from 'react';
import { queryCache } from 'react-query';
import Papa from 'papaparse';
import _ from 'lodash';
import { ValidationErrorItem, ParseResult, delimiters, validateRow } from 'src/utils/csv';
import { ScenarioCsvRow, mapCsvRowToScenario, pathNames, scenarioCsvValidationSchema } from './util';
import { selectUserOption } from 'src/utils/misc';
import { useAuth } from 'src/state/auth';
import useWorkspaceVariables from 'src/hooks/useWorkspaceVariables';
import { mpEvent } from 'src/utils/mixpanel/useMixPanel';
import { MPEvents } from 'src/utils/mixpanel/types';
import { KeyType, useCache } from 'src/graphql';
import {
  ScenarioInput,
  useUpsertAssessmentVariablesMutation,
  useUpsertScenariosMutation,
} from 'src/graphql/_generated';
import { useAssessmentVariables } from 'src/hooks/useVariables';
import { QKeys, Variable } from 'src/api/types';
import { FormulaVariableInfo } from 'src/utils/formula';
import { CsvVariable } from 'src/components/VariableCSVReader/types';
import { ManualVariable } from './MissingVariablesForm';

interface UseScenarioCsvUploadProps {
  assessmentId: string;
  setIsScvDirty: (isDirty: boolean) => void;
  invalidateQueries: KeyType[];
}

export const useScenarioCsvUpload = ({ assessmentId, setIsScvDirty, invalidateQueries }: UseScenarioCsvUploadProps) => {
  const { usersOptios } = useAuth();
  const { variables: workspaceVariables } = useWorkspaceVariables();
  const { assessmentVariables, refetch: refetchAssessmentVariables } = useAssessmentVariables({ assessmentId });

  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState('');
  const [isUploadSuccessful, setIsUploadSuccessful] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationErrorItem[]>([]);
  const [validScenarios, setValidScenarios] = useState<ScenarioInput[]>([]);
  const [formulaVariables, setFormulaVariables] = useState<FormulaVariableInfo[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [rawCsvRows, setRawCsvRows] = useState<ScenarioCsvRow[]>([]);
  const [csvVariables, setCsvVariables] = useState<CsvVariable[]>([]);
  const [manualVariables, setManualVariables] = useState<ManualVariable[]>([]);

  const { refetchQuery } = useCache();

  const allVariables = useMemo((): Variable[] => {
    const variablesMap = new Map<number, Variable>();
    workspaceVariables.forEach((v) => variablesMap.set(v.id, v));
    assessmentVariables?.forEach((v) => variablesMap.set(v.id, v));
    return Array.from(variablesMap.values());
  }, [workspaceVariables, assessmentVariables]);

  const [upsertScenarios] = useUpsertScenariosMutation();
  const [upsertAssessmentVariables] = useUpsertAssessmentVariablesMutation();

  const missingVariableNames = useMemo(() => {
    return formulaVariables.filter((v) => !v.found).map((v) => v.name);
  }, [formulaVariables]);

  const parseCSVToJSON = useCallback(
    (file: File) => {
      const tryParse = (delimiter: string) =>
        new Promise<ParseResult<ScenarioCsvRow>>((resolve, reject) => {
          Papa.parse(file, {
            header: true,
            dynamicTyping: false,
            skipEmptyLines: true,
            delimiter,
            complete: resolve,
            error: reject,
          });
        });

      (async () => {
        let result: ParseResult<ScenarioCsvRow> | null = null;

        for (const delim of delimiters) {
          const parsed = await tryParse(delim);
          if (parsed.data && Object.keys(parsed.data[0] || {}).length > 2) {
            result = parsed;
            break;
          }
        }

        if (!result || !result.data?.length) {
          setValidationErrors([
            { rowIndex: 0, errors: [{ path: 'Data', message: 'No scenario data found in the file.' }] },
          ]);
          return;
        }

        if (result.data.length > 60) {
          setError('The number of scenarios exceeds the limit of 60.');
          setFile(null);
          return;
        }

        setRawCsvRows(result.data);

        const rawValidationPromises = result.data.map((row, idx) => {
          return validateRow({ row, rowIndex: idx + 1, schema: scenarioCsvValidationSchema, pathNames });
        });
        const rawValidationResults = await Promise.all(rawValidationPromises);
        const rawSchemaErrorLog = rawValidationResults.filter((el) => el?.errors?.length) as ValidationErrorItem[];

        const mappedResults = result.data.map((row, idx) => {
          const result = mapCsvRowToScenario(row, allVariables);

          return result;
        });

        const allFoundVars = mappedResults.flatMap((r) => r.foundVariables);
        const uniqueVars = allFoundVars.filter((v, i, arr) => arr.findIndex((x) => x.snakeName === v.snakeName) === i);
        setFormulaVariables(uniqueVars);

        const missingVars = uniqueVars.filter((v) => !v.found);

        const mappingErrorLog: ValidationErrorItem[] = mappedResults
          .map((r, idx) => ({
            rowIndex: idx + 1,
            errors: r.mappingErrors.map((e) => ({ path: pathNames[e.field] || e.field, message: e.error })),
          }))
          .filter((r) => r.errors.length > 0);

        const scenarios = mappedResults.map((r) => r.scenario);

        const schemaErrorLog = rawSchemaErrorLog;

        const allErrors = [...mappingErrorLog, ...schemaErrorLog];

        if (allErrors.length > 0) {
          setValidationErrors(allErrors);
          setIsUploadSuccessful(false);
        } else if (missingVars.length > 0) {
          setValidScenarios(scenarios);
          setValidationErrors([]);
          setError('');
        } else {
          setValidScenarios(scenarios);
          setValidationErrors([]);
          setError('');
        }
      })();
    },
    [allVariables],
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      mpEvent(MPEvents.FileLoaded, {
        modal: 'Scenario add modal',
        tags: ['SCENARIO'],
      });
      if (acceptedFiles?.length > 0) {
        const selectedFile = acceptedFiles[0];
        setFile(selectedFile);
        setError('');
        setIsUploadSuccessful(false);
        setValidationErrors([]);
        setFormulaVariables([]);
        parseCSVToJSON(selectedFile);
        setIsScvDirty(true);
      }
    },
    [parseCSVToJSON, setIsScvDirty],
  );

  const getVariablesToCreate = useCallback((): { name: string; value: number | null; currency: boolean }[] => {
    const result: { name: string; value: number | null; currency: boolean }[] = [];
    const addedSnakeNames = new Set<string>();

    csvVariables.forEach((csvVar) => {
      const csvSnakeName = _.snakeCase(csvVar.name);
      const isMissing = missingVariableNames.some((name) => _.snakeCase(name) === csvSnakeName);
      if (isMissing && !addedSnakeNames.has(csvSnakeName)) {
        result.push({ name: csvVar.name, value: csvVar.value ?? null, currency: csvVar.currency });
        addedSnakeNames.add(csvSnakeName);
      }
    });

    manualVariables.forEach((manualVar) => {
      if (!addedSnakeNames.has(manualVar.snakeName) && manualVar.value !== null) {
        result.push({ name: manualVar.name, value: manualVar.value, currency: manualVar.currency });
        addedSnakeNames.add(manualVar.snakeName);
      }
    });

    return result;
  }, [csvVariables, manualVariables, missingVariableNames]);

  const saveScenarios = useCallback(async () => {
    mpEvent(MPEvents.ButtonClick, {
      button: 'Upload',
      modal: 'Scenario add modal',
      tags: ['SCENARIO'],
    });

    setIsUploading(true);

    try {
      let updatedVariables = allVariables;

      const variablesToCreate = getVariablesToCreate();

      if (variablesToCreate.length > 0) {
        await upsertAssessmentVariables({
          variables: {
            assessmentId,
            variables: variablesToCreate.map((v) => ({
              name: v.name,
              value: v.value,
              currency: v.currency,
              isGlobal: false,
            })),
          },
        });

        const newAssessmentVars = await refetchAssessmentVariables();
        if (newAssessmentVars) {
          const variablesMap = new Map<number, Variable>();
          workspaceVariables.forEach((v) => variablesMap.set(v.id, v));
          newAssessmentVars.forEach((v) => variablesMap.set(v.id, v));
          updatedVariables = Array.from(variablesMap.values());
        }
      }

      const remappedResults = rawCsvRows.map((row) => mapCsvRowToScenario(row, updatedVariables));
      const scenarios = remappedResults.map((r) => r.scenario);

      const processedScenarios = scenarios.map((scenario) => {
        if (!scenario.riskOwner?.text) {
          return scenario;
        }
        const { userAdd } = selectUserOption({
          usersOptios,
          selectedUser: { text: scenario.riskOwner.text },
        });
        return { ...scenario, riskOwner: userAdd };
      });

      await upsertScenarios({ variables: { assessmentId, scenariosData: processedScenarios } });

      queryCache.invalidateQueries([QKeys.Audit]);
      refetchQuery({ include: invalidateQueries });
      setIsScvDirty(false);
      setIsUploadSuccessful(true);
      setManualVariables([]);
      setCsvVariables([]);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Something went wrong';
      setError(errorMessage);
    } finally {
      setIsUploading(false);
    }
  }, [
    allVariables,
    assessmentId,
    getVariablesToCreate,
    invalidateQueries,
    rawCsvRows,
    refetchAssessmentVariables,
    refetchQuery,
    setIsScvDirty,
    upsertAssessmentVariables,
    upsertScenarios,
    usersOptios,
    workspaceVariables,
  ]);

  const resetUpload = useCallback(() => {
    setIsUploadSuccessful(false);
    setValidationErrors([]);
    setError('');
    setFile(null);

    setFormulaVariables([]);
    setCsvVariables([]);
    setRawCsvRows([]);
    setManualVariables([]);
  }, []);

  return {
    // State
    state: {
      file,
      error,
      isUploadSuccessful,
      validationErrors,
      validScenarios,
      isUploading,
      csvVariables,
      manualVariables,
      missingVariableNames,
      workspaceVariables,
      assessmentVariables,
    },

    // Handlers
    handlers: { onDrop, saveScenarios, resetUpload, setManualVariables },
  };
};
